import { FormattedMessage, useIntl } from '@kyruus/intl';
import React, { useEffect, useState } from 'react';
import { Alert } from '@biome-design-system/ui/Alert';
import messages from '../../lib/messages';
import ProfileContact from '../ProfileContact/profile-contact';
import LocationAddress from '../ProviderLocations/location-address';
import { extractBookAsTypeIds } from '../../lib/utils';
import {
  getProviderClinicAvailability,
  getCurrentDay,
  getCurrentTime
} from '../ProviderLocations/utils';
import { ShimmeringLoaderBar } from '@kyruus/loaders-and-spinners';
import {
  StyledWaitTimeSection,
  StyledWaitTimeHeader,
  StyledWaitTime,
  StyledLocation,
  StyledLocationName,
  StyledButtonLink,
  StyledLoader
} from './styles';
import {
  Mloc,
  PMCConfig,
  ProviderMatchProviderLocationV9,
  ProviderMatchProviderV9
} from '@kyruus/types';
import { LoggingFunction } from '@kyruus/provider-components/provider-components/dist/ProviderAvailability/types';

const formatWaitTime = (waitTimeInSeconds: string) => {
  const seconds = parseInt(waitTimeInSeconds, 10);
  if (isNaN(seconds) || seconds <= 59) {
    return (
      <FormattedMessage
        {...messages.estimatedWaitTimeMinutes}
        values={{ minutes: 0 }}
      />
    );
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0 && minutes > 0) {
    return (
      <FormattedMessage
        {...messages.estimatedWaitTimeHoursAndMinutes}
        values={{ hours: hours, minutes: minutes }}
      />
    );
  }
  if (hours > 0 && minutes === 0) {
    return (
      <FormattedMessage
        {...messages.estimatedWaitTimeHours}
        values={{ hours: hours }}
      />
    );
  }
  if (minutes > 0 && hours === 0) {
    return (
      <FormattedMessage
        {...messages.estimatedWaitTimeMinutes}
        values={{ minutes: minutes }}
      />
    );
  }
  return null;
};

function EstimatedWaitTimeSection({
  provider,
  locations,
  mlocations,
  config,
  track,
  getWaitTimeCallbackFunction = (
    _locationEhrId,
    _sourceSystem,
    _bookAsTypeIds
  ) => null,
  waitTimeMap
}: {
  provider: ProviderMatchProviderV9;
  locations: ProviderMatchProviderLocationV9[];
  mlocations: Record<string, Mloc>;
  config: PMCConfig;
  track: LoggingFunction;
  getWaitTimeCallbackFunction: (
    locationEhrId: string,
    sourceSystem: string,
    bookAsTypeIds: string[]
  ) => void;
  waitTimeMap: Record<string, string>;
}) {
  const intl = useIntl();
  const location = locations?.[0];
  const locationEhrId = location?.ehr_location_id ?? '';
  const sourceSystem = location?.source_system ?? '';
  const bookAsTypeIds = extractBookAsTypeIds(provider, sourceSystem);
  const waitTime = waitTimeMap?.[locationEhrId];
  const marketableLocationId = location?.primary_marketable_location_id;

  const periods = marketableLocationId
    ? (mlocations[marketableLocationId]?.hours_of_operation?.periods ?? [])
    : [];
  const locationTimezone = location?.timezone ?? undefined;
  const currentDay = getCurrentDay('dddd', locationTimezone);
  const currentTime = getCurrentTime(locationTimezone);
  const clinicAvailability = getProviderClinicAvailability(
    periods,
    currentDay,
    currentTime,
    intl
  );
  const haveAvailabilityInfo =
    clinicAvailability && clinicAvailability.length > 0;
  const isClosed = haveAvailabilityInfo
    ? clinicAvailability.length == 2
    : undefined;
  const clinicClosedMessage = clinicAvailability?.[0];

  const [waitTimeIsError, setWaitTimeIsError] = useState(false);
  const [waitTimeIsLoading, setWaitTimeIsLoading] = useState(true);

  useEffect(() => {
    const fetchWaitTime = async () => {
      if (locationEhrId && sourceSystem) {
        try {
          await getWaitTimeCallbackFunction(
            locationEhrId,
            sourceSystem,
            bookAsTypeIds
          );
        } catch (error) {
          setWaitTimeIsError(true);
        } finally {
          setWaitTimeIsLoading(false);
        }
      }
    };
    fetchWaitTime();
  }, []);

  return (
    <StyledWaitTimeSection
      aria-labelledby="EstimatedWaitTimeHeader"
      id="estimated-wait-time-section"
    >
      <StyledWaitTimeHeader id="EstimatedWaitTimeHeader">
        <FormattedMessage {...messages.estimatedWaitTimeHeader} />
      </StyledWaitTimeHeader>
      {waitTimeIsLoading ? (
        <StyledLoader>
          <ShimmeringLoaderBar height={30} width={100} />
          <ShimmeringLoaderBar height={30} width={100} />
        </StyledLoader>
      ) : waitTimeIsError ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={(theme) => ({
            marginBottom: theme.spacing(2)
          })}
        >
          <FormattedMessage {...messages.estimatedWaitTimeErrorMsg} />
        </Alert>
      ) : (
        <>
          <StyledWaitTime id="wait-time-display">
            {isClosed ? clinicClosedMessage : formatWaitTime(waitTime)}
          </StyledWaitTime>

          <StyledLocation id="location-info">
            <StyledLocationName>{location.name}</StyledLocationName>
            <LocationAddress location={location} config={config} />
          </StyledLocation>

          <StyledButtonLink
            href={provider.external_wait_time_url}
            mode="primary"
          >
            <FormattedMessage {...messages.externalWaitTimeLinkText} />
          </StyledButtonLink>
        </>
      )}
      <ProfileContact provider={provider} track={track} />
    </StyledWaitTimeSection>
  );
}

export default EstimatedWaitTimeSection;
